import React, { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { X, ArrowUpDown, Check } from "lucide-react";

const TransactionListComponents = ({
  transactions,
  apps,
  onUpdateTransaction,
  onDeleteTransaction,
}) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [filter, setFilter] = useState({
    app: "",
    month: "",
    year: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [editedTransactions, setEditedTransactions] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const itemsPerPage = 25;

  const years = [
    ...new Set(transactions.map((t) => new Date(t.date).getFullYear())),
  ];
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      if (filter.app && transaction.appId._id !== filter.app) return false;
      if (filter.month) {
        const monthIndex = months.indexOf(filter.month);
        if (transactionDate.getMonth() !== monthIndex) return false;
      }
      if (
        filter.year &&
        transactionDate.getFullYear() !== parseInt(filter.year)
      )
        return false;
      return true;
    });
  }, [transactions, filter, months]);

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "casino") {
        aValue = a.appId.name;
        bValue = b.appId.name;
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredTransactions, sortConfig]);

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedTransactions, currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderSortIcon = (columnKey) => {
    return (
      <ArrowUpDown
        className={`inline ml-2 h-4 w-4 ${
          sortConfig.key === columnKey ? "opacity-100" : "opacity-50"
        } cursor-pointer`}
      />
    );
  };

  const exportToCSV = () => {
    const headers = ["Casino", "Type", "Amount", "Date"];
    const csvData = sortedTransactions.map((transaction) => [
      transaction.appId.name,
      transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1),
      `$${Math.abs(transaction.amount).toFixed(2)}`,
      new Date(transaction.date).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    const now = new Date();
    const dateString = now
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .slice(0, 19);
    const filename = `Transactions-${dateString}.csv`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEdit = (field, value, transaction) => {
    const parsedValue = ["amount"].includes(field)
      ? parseFloat(value) || 0
      : value;

      setEditedTransactions((prev) => ({
      ...prev,
      [transaction._id]: {
        ...(prev[transaction._id] || {}),
        [field]: parsedValue,
        _id: transaction._id,
        appId: transaction.appId._id,
        userId: transaction.userId
      },
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const updatesArray = Object.values(editedTransactions);

      await onUpdateTransaction(updatesArray);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      setEditedTransactions({}); // Clear edited notes after saving
    } catch (error) {
      console.error("Error saving transactions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card style={{ backgroundColor: "#FFFFFF" }}>
      <CardHeader>
        <CardTitle>Transactions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={filter.app}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, app: e.target.value }));
                setCurrentPage(1);
              }}
              className="p-2 border rounded bg-white"
            >
              <option value="">All Casinos</option>
              {apps.map((app) => (
                <option key={app._id} value={app._id}>
                  {app.name}
                </option>
              ))}
            </select>

            <select
              value={filter.month}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, month: e.target.value }));
                setCurrentPage(1);
              }}
              className="p-2 border rounded bg-white"
            >
              <option value="">All Months</option>
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>

            <select
              value={filter.year}
              onChange={(e) => {
                setFilter((prev) => ({ ...prev, year: e.target.value }));
                setCurrentPage(1);
              }}
              className="p-2 border rounded bg-white"
            >
              <option value="">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <div className="flex gap-2">
              <button
                onClick={exportToCSV}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Export CSV
              </button>
              <button
                onClick={handleSaveChanges}
                disabled={
                  Object.keys(editedTransactions).length === 0 || isSaving
                }
                className={`
                  px-4 py-2 bg-blue-500 text-white rounded flex items-center justify-center gap-2 w-full sm:w-auto
                  ${
                    Object.keys(editedTransactions).length === 0 || isSaving
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-blue-600"
                  }
                  transition-colors
                `}
              >
                <span>Save Changes</span>
                {isSaving && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {saveSuccess && !isSaving && (
                  <Check className="h-4 w-4 text-white" />
                )}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gray-50">
                  <th
                    onClick={() => handleSort("casino")}
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  >
                    Casino {renderSortIcon("casino")}
                  </th>
                  <th
                    onClick={() => handleSort("type")}
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  >
                    Type {renderSortIcon("type")}
                  </th>
                  <th
                    onClick={() => handleSort("amount")}
                    className="p-3 text-right font-medium cursor-pointer hover:bg-gray-100"
                  >
                    Amount {renderSortIcon("amount")}
                  </th>
                  <th
                    onClick={() => handleSort("date")}
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-100"
                  >
                    Date {renderSortIcon("date")}
                  </th>
                  <th className="p-3 text-left font-medium">Notes</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((transaction) => (
                  <tr key={transaction._id} className="border-t">
                    <td className="p-3">{transaction.appId.name}</td>
                    <td
                      className={`p-3 ${
                        transaction.type === "deposit"
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {transaction.type.charAt(0).toUpperCase() +
                        transaction.type.slice(1)}
                    </td>
                    <td
                      className={`p-3 text-right ${
                        transaction.type === "deposit"
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      ${Math.abs(transaction.amount).toFixed(2)}
                    </td>
                    <td className="p-3">
                      {new Date(transaction.date).toLocaleDateString("en-US", {
                        timeZone: "UTC",
                      })}
                    </td>
                    <td className="p-3">
                      <textarea
                        value={
                          editedTransactions[transaction._id]?.notes !== undefined
                            ? editedTransactions[transaction._id].notes
                            : transaction.notes || ""
                        }
                        onChange={(e) =>
                          handleEdit(
                            "notes",
                            e.target.value,
                            transaction
                          )
                        }
                        className="w-full border rounded resize-y text-sm bg-white p-1"
                        rows={2}
                      />
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => onDeleteTransaction(transaction._id)}
                        className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-sm text-gray-600">
              Total Transactions: {sortedTransactions.length}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-100 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionListComponents;
